/**
 * Microsoft Teams authentication helper.
 *
 * Provides Teams SSO integration with fallback detection.
 * When running inside Teams, uses Teams SSO for automatic login.
 * When running in browser, falls back to MSAL.
 */

import * as microsoftTeams from "@microsoft/teams-js";

/** Track initialization state */
let teamsInitialized = false;
let teamsInitPromise: Promise<void> | null = null;

/**
 * Check if the app is running inside Microsoft Teams.
 *
 * Uses Teams SDK's isInitialized check after attempting initialization.
 */
export async function isInTeams(): Promise<boolean> {
  try {
    console.log("[Teams] Checking if running in Teams...");
    await initializeTeams();
    const context = await microsoftTeams.app.getContext();
    const result = !!context.app.host.name;
    console.log(
      "[Teams] isInTeams result:",
      result,
      "host:",
      context.app.host.name,
    );
    return result;
  } catch (error) {
    console.log("[Teams] isInTeams check failed:", error);
    return false;
  }
}

/**
 * Initialize Microsoft Teams SDK.
 *
 * Must be called before using any Teams functionality.
 * Safe to call multiple times - will only initialize once.
 */
export async function initializeTeams(): Promise<void> {
  if (teamsInitialized) return;

  if (teamsInitPromise) {
    return teamsInitPromise;
  }

  teamsInitPromise = (async () => {
    try {
      await microsoftTeams.app.initialize();
      teamsInitialized = true;
      console.info("[Teams] SDK initialized successfully");
    } catch (error) {
      console.info("[Teams] Not running in Teams environment");
      throw error;
    }
  })();

  return teamsInitPromise;
}

/**
 * Get authentication token from Teams SSO.
 *
 * This uses the Teams SSO flow to silently get an access token
 * using the user's Teams credentials.
 *
 * @returns Access token string or null if unavailable
 */
export async function getTeamsToken(): Promise<string | null> {
  try {
    await initializeTeams();

    const token = await microsoftTeams.authentication.getAuthToken({
      // Request token for our Azure AD app
      // Teams will return an access token if the user is signed in
      silent: true,
    });

    console.info("[Teams] SSO token acquired successfully");
    return token;
  } catch (error) {
    console.warn("[Teams] Failed to get SSO token:", error);
    return null;
  }
}

/**
 * Authentication context type for the app.
 */
export interface AuthContext {
  isTeams: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  userInfo: {
    name?: string;
    email?: string;
    id?: string;
  } | null;
}

/**
 * Get current Teams context (user info, theme, etc.)
 */
export async function getTeamsContext(): Promise<microsoftTeams.app.Context | null> {
  try {
    await initializeTeams();
    return await microsoftTeams.app.getContext();
  } catch {
    return null;
  }
}

/**
 * Notify Teams that the app is ready.
 * Call this after the app has finished loading.
 */
export async function notifyTeamsAppLoaded(): Promise<void> {
  try {
    if (teamsInitialized) {
      await microsoftTeams.app.notifySuccess();
    }
  } catch (error) {
    console.warn("[Teams] Failed to notify app loaded:", error);
  }
}

/**
 * Register a handler for Teams theme changes.
 */
export function onTeamsThemeChange(handler: (theme: string) => void): void {
  if (teamsInitialized) {
    microsoftTeams.app.registerOnThemeChangeHandler(handler);
  }
}
